import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import useOperationalStore from '../store/useOperationalStore';
import useShopSettings from '../settings/useShopSettings';
import { getCurrentRole } from '../../shared/permissions';
import { hasRoadService } from '../engine/entitlements';
import { moneyQueues } from '../engine/estimateMoney';
import rekonMark from '../../Assets/Rekon R.png';
import GlobalSearch from './GlobalSearch';
import './GlobalHeader.css';

// The top bar IS the nav (Phase 2): five workspaces, frozen. New features
// land as job-card tabs or workspace views — never as new nav items.
const WORKSPACES = [
  { id: 'flow',      label: 'Flow',      route: '/flow' },
  { id: 'schedule',  label: 'Schedule',  route: '/schedule' },
  { id: 'road',      label: 'Road',      route: '/road' },
  { id: 'money',     label: 'Money',     route: '/money' },
  { id: 'customers', label: 'Customers', route: '/customers' },
];

// Which workspace a pathname belongs to (for the active tab). Job pages are
// part of the Flow workspace — the board is where you came from.
function activeWorkspace(pathname) {
  if (pathname.startsWith('/schedule'))  return 'schedule';
  if (pathname.startsWith('/road'))      return 'road';
  if (pathname.startsWith('/money'))     return 'money';
  if (pathname.startsWith('/customers')) return 'customers';
  if (pathname.startsWith('/flow') || pathname.startsWith('/ro/') || pathname === '/' || pathname.startsWith('/home')) return 'flow';
  return null;
}

const ROLE_META = {
  admin:          { display: 'Admin',           initials: 'A'  },
  manager:        { display: 'Manager',         initials: 'M'  },
  service_writer: { display: 'Service Writer',  initials: 'SW' },
  service_manager:{ display: 'Service Manager', initials: 'SM' },
  technician:     { display: 'Technician',      initials: 'T'  },
  tech:           { display: 'Technician',      initials: 'T'  },
  dispatcher:     { display: 'Dispatcher',      initials: 'D'  },
  parts:          { display: 'Parts',           initials: 'P'  },
  accounting:     { display: 'Accounting',      initials: 'AC' },
};

export default function GlobalHeader({ onAddTruck, onManageTechs, customerCheckIns = [] }) {
  const trucks        = useOperationalStore(s => s.trucks);
  const setActiveView = useOperationalStore(s => s.setActiveView);
  const settings      = useShopSettings(s => s.settings);
  const branding      = settings.branding;

  const [gearOpen,  setGearOpen]  = useState(false);
  const [userOpen,  setUserOpen]  = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    fetchAuthSession()
      .then(({ tokens }) => setUserEmail(tokens?.idToken?.payload?.email || ''))
      .catch(() => {});
  }, []);

  async function handleSignOut() {
    window.localStorage.removeItem('rekon_role');
    try { await signOut(); } catch {}
    window.location.href = '/';
  }

  const gearRef = useRef(null);
  const userRef = useRef(null);

  const role     = getCurrentRole();
  const roleMeta = ROLE_META[role] || { display: role || 'User', initials: (role || 'U').slice(0, 2).toUpperCase() };
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = activeWorkspace(pathname);

  const showRoad = hasRoadService(settings);
  const q = moneyQueues(trucks);
  const moneyCount = q.unapproved.length + q.unbilled.length + q.unpaid.length;
  // The three queue counts, surfaced on the nav item itself (badge = total,
  // tooltip = the breakdown) so the chrome nags the specific stall.
  const moneyBreakdown = `${q.unapproved.length} unapproved · ${q.unbilled.length} unbilled · ${q.unpaid.length} unpaid`;

  // Close dropdowns on outside click
  useEffect(() => {
    function onMouse(e) {
      if (gearRef.current && !gearRef.current.contains(e.target)) setGearOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    }
    if (gearOpen || userOpen) document.addEventListener('mousedown', onMouse);
    return () => document.removeEventListener('mousedown', onMouse);
  }, [gearOpen, userOpen]);

  function goDisplay() {
    setActiveView('command');
    navigate('/flow?view=command');
  }

  const gearGo = (fn) => () => { setGearOpen(false); fn(); };

  return (
    <header className="global-header">

      {/* ── Left: mark + the five workspaces ── */}
      <div className="gh-left">
        <button className="gh-mark" onClick={() => navigate('/flow')} title="Flow Board">
          <img src={branding.logoUrl || branding.logoBase64 || rekonMark} alt="REKON" />
        </button>

        <nav className="gh-nav" aria-label="Workspaces">
          {WORKSPACES.map(ws => {
            if (ws.id === 'road' && !showRoad) return null;
            return (
              <button
                key={ws.id}
                className={`gh-nav-item${current === ws.id ? ' gh-nav-item--active' : ''}`}
                onClick={() => navigate(ws.route)}
                title={ws.id === 'money' && moneyCount > 0 ? moneyBreakdown : undefined}
              >
                {ws.label}
                {ws.id === 'money' && moneyCount > 0 && (
                  <span className="gh-nav-badge">{moneyCount}</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Center: global search ── */}
      <GlobalSearch onAddTruck={onAddTruck} />

      {/* ── Right: new RO + display mode + settings + user ── */}
      <div className="gh-right">

        {onAddTruck && (
          <button className="gh-new-ro" onClick={onAddTruck} title="New Repair Order">
            + New RO{customerCheckIns.length > 0 ? ` (${customerCheckIns.length})` : ''}
          </button>
        )}

        <button className="gh-icon-btn" onClick={goDisplay} title="Display mode (wall TV)">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="13" rx="2"/><polyline points="17 2 12 7 7 2"/>
          </svg>
        </button>

        <div className="gh-menu-wrap" ref={gearRef}>
          <button className="gh-icon-btn" onClick={() => setGearOpen(v => !v)} title="Settings">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.14.34.4.63.73.79H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>

          {gearOpen && (
            <div className="gh-dropdown">
              <button className="gh-dd-item" onClick={gearGo(() => navigate('/users'))}>Users</button>
              {onManageTechs && (
                <button className="gh-dd-item" onClick={gearGo(onManageTechs)}>Manage Team</button>
              )}
              <button className="gh-dd-item" onClick={gearGo(() => { setActiveView('history'); navigate('/flow?view=history'); })}>History</button>
              <button className="gh-dd-item" onClick={gearGo(() => navigate('/billing'))}>Billing</button>
              <button className="gh-dd-item" onClick={gearGo(() => navigate('/customize'))}>Settings</button>
            </div>
          )}
        </div>

        {/* User chip */}
        <div className="gh-user-wrap" ref={userRef}>
          <button className="gh-user-btn" onClick={() => setUserOpen(v => !v)} title="User menu">
            <div className="gh-avatar">{roleMeta.initials}</div>
            <div className="gh-user-info">
              <span className="gh-user-name">{roleMeta.display}</span>
              <span className="gh-user-role">Logged In</span>
            </div>
            <svg className="gh-user-caret" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {userOpen && (
            <div className="gh-dropdown">
              <div className="gh-dd-role-info">
                <span className="gh-dd-role-label">Role</span>
                <span className="gh-dd-role-val">{roleMeta.display}</span>
              </div>
              {userEmail && (
                <div className="gh-dd-role-info">
                  <span className="gh-dd-role-label">Account</span>
                  <span className="gh-dd-role-val gh-dd-email">{userEmail}</span>
                </div>
              )}
              <button className="gh-dd-item gh-dd-signout" onClick={handleSignOut}>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
